import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface FieldProps {
  field: any;
  value: any;
  onChange: (name: string, value: any) => void;
  formValues: Record<string, any>;
  darkMode: boolean;
}

const Field: React.FC<FieldProps> = ({ field, value, onChange, formValues, darkMode }) => {
  // Check if field should be displayed based on dependencies
  if (field.depends_on) {
    const dependsOnField = field.depends_on.field;
    const dependsOnValue = field.depends_on.value;
    
    if (dependsOnValue !== 'language') { // Special case for code editor
      if (formValues[dependsOnField] !== dependsOnValue) {
        return null;
      }
    }
  }
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    onChange(field.name, e.target.value);
  };
  
  const baseInputClasses = `w-full px-3 py-2 border rounded-md ${
    darkMode 
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  }`;
  
  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          className={baseInputClasses}
          value={value || ''}
          onChange={handleChange}
          placeholder={field.placeholder}
          required={field.required}
        />
      );
      
    case 'textarea':
      return (
        <textarea
          className={`${baseInputClasses} h-32`}
          value={value || ''}
          onChange={handleChange}
          placeholder={field.placeholder}
          required={field.required}
        />
      );
      
    case 'markdown':
      return (
        <textarea
          className={`${baseInputClasses} h-64 font-mono`}
          value={value || ''}
          onChange={handleChange}
          placeholder={field.placeholder}
          required={field.required}
        />
      );
      
    case 'code':
      return (
        <textarea
          className={`${baseInputClasses} h-64 font-mono`}
          value={value || ''}
          onChange={handleChange}
          placeholder={field.placeholder || `// ${formValues.language || 'code'} here`}
          required={field.required}
        />
      );
      
    case 'select':
      return (
        <select
          className={baseInputClasses}
          value={value || field.default || ''}
          onChange={handleChange}
          required={field.required}
        >
          {field.options?.map((option: any) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
      
    case 'number':
      return (
        <input
          type="number"
          className={baseInputClasses}
          value={value || ''}
          onChange={handleChange}
          min={field.min}
          max={field.max}
          step={field.step || 1}
          required={field.required}
        />
      );
      
    case 'file':
      return (
        <div>
          <input
            type="file"
            className="w-full"
            accept={field.accept}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                // Handle file upload
                const reader = new FileReader();
                reader.onload = (event) => {
                  onChange(field.name, event.target?.result);
                };
                reader.readAsDataURL(file);
              }
            }}
          />
        </div>
      );
      
    default:
      return <div>Unsupported field type: {field.type}</div>;
  }
};

interface DynamicFormProps {
  schema: any;
  onSubmit: (values: Record<string, any>) => void;
  initialValues?: Record<string, any>;
  submitLabel?: string;
  isSubmitting?: boolean;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  schema,
  onSubmit,
  initialValues = {},
  submitLabel = 'Submit',
  isSubmitting = false
}) => {
  const { darkMode } = useTheme();
  const [values, setValues] = useState<Record<string, any>>({});
  
  // Use a ref to track if we've already initialized the values
  const initializedRef = React.useRef(false);

  // Initialize form with default values from schema and any provided initialValues
  useEffect(() => {
    if (initializedRef.current) return;
    
    const defaultValues: Record<string, any> = {};
    
    // Set defaults from schema
    if (schema && schema.fields) {
      schema.fields.forEach((field: any) => {
        if (field.default !== undefined) {
          defaultValues[field.name] = field.default;
        }
      });
    }
    
    // Override with initialValues
    setValues({ ...defaultValues, ...initialValues });
    initializedRef.current = true;
  }, []);

  // Reset the initialization flag when schema or initialValues change significantly
  useEffect(() => {
    initializedRef.current = false;
  }, [schema?.fields?.length, Object.keys(initialValues).length]);
  
  const handleChange = (name: string, value: any) => {
    setValues(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Process values before submitting
    const processedValues = { ...values };
    
    // Process textarea fields that should be converted to arrays
    schema.fields.forEach((field: any) => {
      if ((field.name === 'strengths' || field.name === 'weaknesses' || 
           field.name === 'suggestions' || field.name === 'components' || 
           field.name === 'next_steps') && 
          processedValues[field.name]) {
        // Convert newline-separated text to array
        processedValues[field.name] = processedValues[field.name]
          .split('\n')
          .map((line: string) => line.trim())
          .filter((line: string) => line.length > 0);
      }
    });
    
    onSubmit(processedValues);
  };
  
  if (!schema || !schema.fields) {
    return <div>No schema provided</div>;
  }
  
  return (
    <form onSubmit={handleSubmit}>
      {schema.fields.map((field: any) => (
        <div key={field.name} className="mb-4">
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          
          <Field
            field={field}
            value={values[field.name]}
            onChange={handleChange}
            formValues={values}
            darkMode={darkMode}
          />
          
          {field.description && (
            <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {field.description}
            </p>
          )}
        </div>
      ))}
      
      <div className="mt-6">
        <button
          type="submit"
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : submitLabel}
        </button>
      </div>
    </form>
  );
};
